import React, { useState } from 'react';
import { Plus, User, Bot, ChevronRight, X, Users, BrainCircuit } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import HumanEmployeeCard from './HumanEmployeeCard';
import AIAgentCard from './AIAgentCard';
import { cn } from '@/lib/utils';

type EmployeeStatus = 'active' | 'onboarding' | 'offboarding' | 'inactive';

interface HumanEmployee {
  id: string;
  name: string;
  role: string;
  avatar: string;
  department: string;
  status: EmployeeStatus;
}

interface AIAgent {
  id: string;
  name: string;
  role: string;
  avatar: string;
  department: string;
  capability: string;
}

const initialHumans: HumanEmployee[] = [
  { id: '1', name: 'Jane Smith', role: 'Chief Marketing Officer', avatar: '', department: 'Marketing', status: 'active' },
  { id: '2', name: 'David Chen', role: 'Senior Developer', avatar: '', department: 'Engineering', status: 'active' },
  { id: '3', name: 'Sophia Rodriguez', role: 'Financial Analyst', avatar: '', department: 'Finance', status: 'onboarding' }
];

const initialAgents: AIAgent[] = [
  { id: '1', name: 'CodeBuddy', role: 'Code Reviewer', avatar: '', department: 'Engineering', capability: 'Reviews code PRs and suggests improvements' },
  { id: '2', name: 'MarketScan', role: 'Market Researcher', avatar: '', department: 'Marketing', capability: 'Analyzes competitor websites and social media' },
  { id: '3', name: 'DocBot', role: 'Documentation Writer', avatar: '', department: 'Operations', capability: 'Creates and updates technical documentation' }
];

const humanRoles = [
  { title: 'Developer', department: 'Engineering' },
  { title: 'Designer', department: 'Product' },
  { title: 'Marketing Specialist', department: 'Marketing' },
  { title: 'Sales Representative', department: 'Sales' },
  { title: 'Financial Analyst', department: 'Finance' },
  { title: 'HR Specialist', department: 'Operations' },
  { title: 'Customer Support', department: 'Operations' }
];

const agentRoles = [
  { title: 'Code Reviewer', department: 'Engineering', capability: 'Reviews code and suggests improvements' },
  { title: 'Market Researcher', department: 'Marketing', capability: 'Analyzes market trends and competitor activity' },
  { title: 'Sales Automator', department: 'Sales', capability: 'Qualifies leads and schedules meetings' },
  { title: 'Documentation Writer', department: 'Operations', capability: 'Creates and maintains documentation' },
  { title: 'Financial Reporter', department: 'Finance', capability: 'Generates financial reports and forecasts' },
  { title: 'Customer Support Bot', department: 'Operations', capability: 'Answers common customer queries' }
];

const TeamPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('humans');
  const [humans, setHumans] = useState<HumanEmployee[]>(initialHumans);
  const [agents, setAgents] = useState<AIAgent[]>(initialAgents);
  const [isAddingHuman, setIsAddingHuman] = useState(false);
  const [isAddingAgent, setIsAddingAgent] = useState(false);
  const [newHumanName, setNewHumanName] = useState('');
  const [newAgentName, setNewAgentName] = useState('');
  const [selectedHumanRole, setSelectedHumanRole] = useState<typeof humanRoles[0] | null>(null);
  const [selectedAgentRole, setSelectedAgentRole] = useState<typeof agentRoles[0] | null>(null);

  const addHuman = () => {
    if (newHumanName && selectedHumanRole) {
      const newHuman: HumanEmployee = {
        id: `h-${Date.now()}`,
        name: newHumanName,
        role: selectedHumanRole.title,
        department: selectedHumanRole.department,
        avatar: '',
        status: 'onboarding'
      };
      setHumans([...humans, newHuman]);
      setNewHumanName('');
      setSelectedHumanRole(null);
      setIsAddingHuman(false);
    }
  };

  const addAgent = () => {
    if (newAgentName && selectedAgentRole) {
      const newAgent: AIAgent = {
        id: `a-${Date.now()}`,
        name: newAgentName,
        role: selectedAgentRole.title,
        department: selectedAgentRole.department,
        avatar: '',
        capability: selectedAgentRole.capability
      };
      setAgents([...agents, newAgent]);
      setNewAgentName('');
      setSelectedAgentRole(null);
      setIsAddingAgent(false);
    }
  };

  return (
    <div className="space-y-6 p-6 pb-16 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Team Management</h1>
          <p className="text-muted-foreground">
            Manage your human employees and AI agents
          </p>
        </div>
      </div>

      <Tabs defaultValue="humans" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full md:w-auto grid-cols-2">
          <TabsTrigger value="humans" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span>Human Employees ({humans.length})</span>
          </TabsTrigger>
          <TabsTrigger value="agents" className="flex items-center gap-2">
            <BrainCircuit className="h-4 w-4" />
            <span>AI Agents ({agents.length})</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="humans" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Human Team Members</h2>
            {!isAddingHuman && (
              <Button onClick={() => setIsAddingHuman(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Hire Employee
              </Button>
            )}
          </div>

          {isAddingHuman && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Hire New Employee</CardTitle>
                <CardDescription>Add a new human employee to your team</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Employee Name</label>
                  <Input 
                    placeholder="Enter employee name" 
                    value={newHumanName} 
                    onChange={(e) => setNewHumanName(e.target.value)} 
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Select Role</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {humanRoles.map((role) => (
                      <Button 
                        key={role.title} 
                        variant={selectedHumanRole?.title === role.title ? "default" : "outline"} 
                        className="justify-start h-auto py-2"
                        onClick={() => setSelectedHumanRole(role)}
                      >
                        <div className="text-left">
                          <div className="font-medium">{role.title}</div>
                          <div className="text-xs text-muted-foreground">{role.department}</div>
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={() => setIsAddingHuman(false)}>Cancel</Button>
                <Button onClick={addHuman} disabled={!newHumanName || !selectedHumanRole}>
                  Hire Employee
                </Button>
              </CardFooter>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {humans.map((human) => (
              <HumanEmployeeCard key={human.id} employee={human} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="agents" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">AI Team Members</h2>
            {!isAddingAgent && (
              <Button onClick={() => setIsAddingAgent(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Create AI Agent
              </Button>
            )}
          </div>

          {isAddingAgent && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Create New AI Agent</CardTitle>
                <CardDescription>Add a new AI agent to your team</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Agent Name</label>
                  <Input 
                    placeholder="Enter agent name" 
                    value={newAgentName} 
                    onChange={(e) => setNewAgentName(e.target.value)} 
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Select Agent Type</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {agentRoles.map((role) => (
                      <Button 
                        key={role.title} 
                        variant={selectedAgentRole?.title === role.title ? "default" : "outline"} 
                        className="justify-start h-auto py-2"
                        onClick={() => setSelectedAgentRole(role)}
                      >
                        <div className="text-left">
                          <div className="font-medium">{role.title}</div>
                          <div className="text-xs text-muted-foreground">{role.department}</div>
                          <div className="text-xs mt-1 opacity-80">{role.capability}</div>
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={() => setIsAddingAgent(false)}>Cancel</Button>
                <Button onClick={addAgent} disabled={!newAgentName || !selectedAgentRole}>
                  Create Agent
                </Button>
              </CardFooter>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map((agent) => (
              <AIAgentCard key={agent.id} agent={agent} />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TeamPage;
